import { eyeIcon } from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React from "react";

import { isDevEnv } from "@excalidraw/common";
import type { Theme } from "@excalidraw/element/types";
import { LanguageList } from "../app-language/LanguageList";
import { saveDebugState } from "./DebugCanvas";
import { useAtom } from "../app-jotai";
import {
  authSessionAtom,
  authDialogStateAtom,
  canvasListDialogStateAtom,
  saveCanvasDialogStateAtom,
  activeCanvasAtom,
  authStorage,
  authApi,
} from "../auth";

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  refresh: () => void;
  onNewCanvas?: () => void;
}> = React.memo((props) => {
  const [session, setSession] = useAtom(authSessionAtom);
  const [activeCanvas, setActiveCanvas] = useAtom(activeCanvasAtom);
  const [, setAuthDialog] = useAtom(authDialogStateAtom);
  const [, setCanvasListDialog] = useAtom(canvasListDialogStateAtom);
  const [, setSaveCanvasDialog] = useAtom(saveCanvasDialogStateAtom);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore
    }
    authStorage.clear();
    setSession({
      user: null,
      accessToken: null,
      isLoading: false,
      error: null,
    });
  };

  return (
    <MainMenu>
      {session.user ? (
        <>
          <MainMenu.ItemCustom className="AppMainMenu__user-item">
            <div className="AppMainMenu__user-card">
              <div className="AppMainMenu__user-card__name-row">
                <span className="AppMainMenu__user-card__name">
                  {session.user.name}
                </span>
                {session.user.role?.toLowerCase() === "admin" && (
                  <span className="AppMainMenu__user-card__badge">ADMIN</span>
                )}
              </div>
              <span className="AppMainMenu__user-card__email">
                {session.user.email}
              </span>
            </div>
          </MainMenu.ItemCustom>
          <MainMenu.Item
            onSelect={() => {
              setActiveCanvas(null);
              props.onNewCanvas?.();
            }}
          >
            New Blank Canvas
          </MainMenu.Item>
          <MainMenu.Item
            onSelect={() => setCanvasListDialog({ isOpen: true })}
          >
            My Cloud Canvases
          </MainMenu.Item>
          <MainMenu.Item
            shortcut="Ctrl+S"
            onSelect={() => setSaveCanvasDialog({ isOpen: true })}
          >
            {activeCanvas ? `Save to Cloud ("${activeCanvas.name}")` : "Save to Cloud"}
          </MainMenu.Item>
          <MainMenu.Item
            onSelect={handleLogout}
          >
            Sign Out
          </MainMenu.Item>
          <MainMenu.Separator />
        </>
      ) : (
        <>
          <MainMenu.Item
            onSelect={() =>
              setAuthDialog({ isOpen: true, initialTab: "login" })
            }
          >
            Sign In / Register
          </MainMenu.Item>
          <MainMenu.Separator />
        </>
      )}

      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      {props.isCollabEnabled && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.Item
        onSelect={() => {
          setActiveCanvas(null);
          props.onNewCanvas?.();
        }}
      >
        Clear canvas
      </MainMenu.Item>
      <MainMenu.Separator />
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      <MainMenu.DefaultItems.Preferences />
      <MainMenu.DefaultItems.ToggleTheme allowSystemTheme theme={props.theme} />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
  );
});
